/**
 * useKioskDataProcessing Hook
 *
 * Transforms raw kiosk data into display-ready format.
 * Handles teacher name resolution, absence mapping, class absences,
 * and lesson entry creation for the Vertretungsplan display.
 *
 * All processing uses useMemo for performance optimization,
 * preventing unnecessary recalculations on re-renders.
 *
 * @module kiosk/hooks/useKioskDataProcessing
 */

import React from "react";
import { fixMojibake } from "../../../utils/textHelpers";

export const useKioskDataProcessing = (data: any) => {
  const periodsMapById = React.useMemo(() => {
    const map = new Map<string, { label: string }>();
    (data?.periods || []).forEach((p: any) => {
      if (p?.id) map.set(p.id, { label: p.label || "" });
    });
    return map;
  }, [data?.periods]);

  // Detect last names that appear more than once among all staff
  const duplicateLastNames = React.useMemo(() => {
    const counts = new Map<string, number>();
    (data?.availableTeachers || []).forEach((t: any) => {
      const last = (t?.last_name || "").trim();
      if (last) counts.set(last, (counts.get(last) || 0) + 1);
    });
    const dupes = new Set<string>();
    counts.forEach((count, name) => {
      if (count >= 2) dupes.add(name);
    });
    return dupes;
  }, [data?.availableTeachers]);

  const teacherKurzelById = React.useMemo(() => {
    const map = new Map<string, string>();
    (data?.availableTeachers || []).forEach((t: any) => {
      const firstName = (t?.first_name || "").trim();
      const lastName = (t?.last_name || "").trim();
      let k = lastName || t?.kurzung || t?.kurzel || "";
      // Disambiguate: "F. LastName" when last name is shared
      if (lastName && duplicateLastNames.has(lastName) && firstName) {
        k = `${firstName[0]}. ${lastName}`;
      }
      if (t?.id && k) map.set(t.id, k);
    });
    return map;
  }, [data?.availableTeachers, duplicateLastNames]);

  const lastNameByKurzel = React.useMemo(() => {
    const map = new Map<string, string>();
    (data?.availableTeachers || []).forEach((t: any) => {
      const k = t?.kurzel || t?.kurzung || "";
      const firstName = (t?.first_name || "").trim();
      const lastName = (t?.last_name || "").trim();
      const last = lastName || t?.name || "";
      if (!k) return;
      let display = last || k;
      // Disambiguate: "F. LastName" when last name is shared
      if (lastName && duplicateLastNames.has(lastName) && firstName) {
        display = `${firstName[0]}. ${lastName}`;
      }
      map.set(k, display);
    });
    return map;
  }, [data?.availableTeachers, duplicateLastNames]);

  // Map full names to disambiguated display names
  // NOTE: We do NOT map bare last names because we can't know which person it is
  const displayByFullName = React.useMemo(() => {
    const map = new Map<string, string>();
    (data?.availableTeachers || []).forEach((t: any) => {
      const firstName = (t?.first_name || "").trim();
      const lastName = (t?.last_name || "").trim();
      const fullName = String(t?.name || `${firstName} ${lastName}`).trim();
      if (!lastName) return;
      if (duplicateLastNames.has(lastName) && firstName) {
        const display = `${firstName[0]}. ${lastName}`;
        if (fullName) map.set(fullName.toLowerCase(), display);
      }
    });
    return map;
  }, [data?.availableTeachers, duplicateLastNames]);

  const nameToKurzel = React.useMemo(() => {
    const map = new Map<string, string>();
    (data?.availableTeachers || []).forEach((t: any) => {
      const full = String(
        t?.name || `${t?.first_name || ""} ${t?.last_name || ""}`,
      ).trim();
      const k = t?.kurzung || t?.kurzel || "";
      if (full && k) map.set(full.toLowerCase(), k);
    });
    return map;
  }, [data?.availableTeachers]);

  const clampName = (name: string, max = 10): string => {
    if (name.length <= max) return name;
    return name.slice(0, max) + "…";
  };

  const displayKurzel = React.useCallback(
    (v: string) => {
      const mapped = lastNameByKurzel.get(v);
      if (mapped) return clampName(mapped);
      const t = String(v || "").trim();
      if (!t) return "";
      // Check displayByFullName for exact match (full name or bare last name)
      const byFull = displayByFullName.get(t.toLowerCase());
      if (byFull) return clampName(byFull);
      if (t.includes(" ")) {
        const parts = t.split(/\s+/);
        const last = parts[parts.length - 1];
        // Check if this last name needs disambiguation
        if (last && duplicateLastNames.has(last) && parts.length >= 2) {
          return clampName(`${parts[0][0]}. ${last}`);
        }
        return clampName(last);
      }
      return clampName(t);
    },
    [lastNameByKurzel, duplicateLastNames, displayByFullName],
  );

  const absenteLehrkraefte = React.useMemo(() => {
    // Create a map for roles
    const roleMap = new Map<string, string>();
    (data?.availableTeachers || []).forEach((t: any) => {
      if (t.id) roleMap.set(t.id, t.role_name);
      const name = String(
        t.name || `${t.first_name || ""} ${t.last_name || ""}`,
      ).trim();
      if (name) roleMap.set(name, t.role_name);
    });

    // Build a map from staff_id to disambiguated display name
    const idToDisplayName = new Map<string, string>();
    (data?.availableTeachers || []).forEach((t: any) => {
      const firstName = (t?.first_name || "").trim();
      const lastName = (t?.last_name || "").trim();
      let displayName = lastName || t?.name || "";
      if (lastName && duplicateLastNames.has(lastName) && firstName) {
        displayName = `${firstName[0]}. ${lastName}`;
      }
      if (t?.id && displayName) idToDisplayName.set(t.id, displayName);
    });

    return (
      (data?.personalAbsences || [])
        // Filter out auto-created absences linked to class trips
        .filter((a: any) => !a.linked_class_absence_id && !a.auto_created)
        .map((a: any) => {
          let role = "";
          if (a.staff_id) role = roleMap.get(a.staff_id) || "";
          if (!role && a.name) role = roleMap.get(a.name) || "";

          // Prefer disambiguated name from staff_id, fallback to lastNameByKurzel
          const displayName =
            (a.staff_id && idToDisplayName.get(a.staff_id)) ||
            lastNameByKurzel.get(a.name) ||
            a.name;

          return {
            name: displayName,
            status: a.status,
            role: role,
            startTime: a.startTime,
            endTime: a.endTime,
          };
        })
    );
  }, [
    data?.personalAbsences,
    lastNameByKurzel,
    data?.availableTeachers,
    duplicateLastNames,
  ]);

  const absenteKlassen = React.useMemo(() => {
    if (!data?.classAbsences) return [];
    return data.classAbsences.map((ca: any) => {
      let stundenangabe = "";

      // Handle partial times
      if (ca.start_time && ca.end_time) {
        const start = String(ca.start_time).slice(0, 5);
        const end = String(ca.end_time).slice(0, 5);
        stundenangabe = `${start} - ${end}`;
      }

      // Append accompanying staff if present
      if (ca.accompanying_staff_names) {
        stundenangabe = stundenangabe
          ? `${stundenangabe} mit ${ca.accompanying_staff_names}`
          : `mit ${ca.accompanying_staff_names}`;
      }

      const reason = ca.destination
        ? `${ca.absence_type} (${ca.destination})`
        : ca.absence_type;

      return {
        klasse: ca.class_name,
        stundenangabe: stundenangabe || reason,
      };
    });
  }, [data?.classAbsences]);

  const absentTagByKurzel = React.useMemo(() => {
    const map = new Map<string, string>();
    (data?.personalAbsences || []).forEach((a: any) => {
      if (a.name && a.status) {
        map.set(a.name, a.status);
      }
    });
    return map;
  }, [data?.personalAbsences]);

  const clampFach = React.useCallback((s?: string) => {
    const t = fixMojibake(String(s || "")).trim();
    return t.length > 8 ? t.slice(0, 8) : t;
  }, []);

  const entries = React.useMemo(() => {
    if (!data?.lessons) return [];
    const lessons = [...data.lessons];

    // Sort lessons
    lessons.sort((a: any, b: any) => {
      const classA = a.class || "";
      const classB = b.class || "";

      // Check if entries are Flexband or have no class
      const isFlexbandOrNoClassA =
        !classA || classA.toLowerCase().includes("flexband");
      const isFlexbandOrNoClassB =
        !classB || classB.toLowerCase().includes("flexband");

      // If one is Flexband/no-class and the other isn't, sort Flexband/no-class to the bottom
      if (isFlexbandOrNoClassA && !isFlexbandOrNoClassB) return 1;
      if (!isFlexbandOrNoClassA && isFlexbandOrNoClassB) return -1;

      // 1. Class (for regular classes)
      const classComp = classA.localeCompare(classB, undefined, {
        numeric: true,
        sensitivity: "base",
      });
      if (classComp !== 0) return classComp;

      // 2. Period
      const periodA = a.periodStart || "";
      const periodB = b.periodStart || "";
      const periodComp = periodA.localeCompare(periodB);
      if (periodComp !== 0) return periodComp;

      // 3. Substitute Teacher
      const subA = a.substitute_primary_teacher_kurzel || "";
      const subB = b.substitute_primary_teacher_kurzel || "";
      return subA.localeCompare(subB, undefined, { sensitivity: "base" });
    });

    // Calculate busy teachers
    const busyTeachersByPeriod = new Map<string, Set<string>>();
    lessons.forEach((l: any) => {
      if (!l.period) return;
      if (!busyTeachersByPeriod.has(l.period)) {
        busyTeachersByPeriod.set(l.period, new Set());
      }
      const busySet = busyTeachersByPeriod.get(l.period)!;
      if (l.substitute_primary_teacher_id)
        busySet.add(l.substitute_primary_teacher_id);
      if (Array.isArray(l.substitute_begleitung_teacher_ids)) {
        l.substitute_begleitung_teacher_ids.forEach((id: string) =>
          busySet.add(id),
        );
      }
    });

    return lessons.map((lesson: any) => {
      let stunde = "";
      if (lesson.period && periodsMapById.has(lesson.period)) {
        stunde = periodsMapById.get(lesson.period)!.label || "";
      } else if (lesson.period) {
        stunde = String(lesson.period);
      }

      const fehlendeKraft: { name: string; isAbsent: boolean }[] = [];
      const primaryKurzel = lesson.original_teacher_kurzel || "";

      const isTeacherBusy = (tid: string) => {
        if (!lesson.period) return false;
        const busySet = busyTeachersByPeriod.get(lesson.period);
        if (!busySet?.has(tid)) return false;

        // If the teacher is substituting IN THIS LESSON, they are not "absent" from this lesson.
        if (tid === lesson.substitute_primary_teacher_id) return false;
        if (
          Array.isArray(lesson.substitute_begleitung_teacher_ids) &&
          lesson.substitute_begleitung_teacher_ids.includes(tid)
        )
          return false;

        return true;
      };

      const absentIds: string[] = Array.isArray(lesson.absent_teacher_ids)
        ? lesson.absent_teacher_ids
        : [];
      const teacherIds: string[] = Array.isArray(lesson.teacher_ids)
        ? lesson.teacher_ids
        : [];
      const teacherKurzels: string[] = Array.isArray(lesson.teacher_kurzels)
        ? lesson.teacher_kurzels
        : [];
      const idToKurzel = new Map<string, string>();

      if (
        teacherIds.length === teacherKurzels.length &&
        teacherIds.length > 0
      ) {
        teacherIds.forEach((tid, idx) =>
          idToKurzel.set(tid, teacherKurzels[idx]),
        );
      }

      // Create a set of all originally scheduled teacher IDs
      const originalTeacherIds = new Set<string>();
      if (lesson.original_primary_teacher_id) {
        originalTeacherIds.add(lesson.original_primary_teacher_id);
      }
      teacherIds.forEach((tid) => originalTeacherIds.add(tid));

      const processedTeachers = new Set<string>();
      const addTeacher = (tid: string, name: string) => {
        if (processedTeachers.has(tid)) return;
        processedTeachers.add(tid);
        const isAbsent = absentIds.includes(tid) || isTeacherBusy(tid);
        fehlendeKraft.push({ name, isAbsent });
      };

      // Use ID-based lookup for disambiguation (teacherKurzelById already produces "F. LastName")
      const primaryDisplay = lesson.original_primary_teacher_id
        ? teacherKurzelById.get(lesson.original_primary_teacher_id) ||
          primaryKurzel
        : primaryKurzel;

      if (lesson.original_primary_teacher_id && primaryDisplay) {
        addTeacher(lesson.original_primary_teacher_id, primaryDisplay);
      }
      teacherIds.forEach((tid) => {
        const k = teacherKurzelById.get(tid) || idToKurzel.get(tid) || "";
        if (k) addTeacher(tid, k);
      });

      const subNames: string[] = [];
      const allTeachersAbsent =
        teacherIds.length > 0 &&
        teacherIds.every((tid) => absentIds.includes(tid));
      const dbCancelled =
        lesson.substitution_reason === "ub_ignored_for_day"
          ? false
          : lesson.substitute_is_cancelled;

      // If explicitly cancelled in DB, respect that decision regardless of teacher absence status
      // Only infer cancellation from teacher absences if NOT explicitly cancelled in DB
      let isCancelled = dbCancelled;
      if (!dbCancelled) {
        // Infer cancellation only if all teachers are absent and no substitute is assigned
        isCancelled =
          allTeachersAbsent &&
          !lesson.substitute_primary_teacher_id &&
          !lesson.substitute_primary_teacher_kurzel;
      }

      if (!isCancelled) {
        // Add substitute teacher to Vertretung column.
        // Show them even if they were an original teacher (e.g. UB promoted to primary)
        // as long as another teacher on the lesson is actually absent.
        const hasAbsentTeacher = absentIds.length > 0;
        if (
          lesson.substitute_primary_teacher_id &&
          (!originalTeacherIds.has(lesson.substitute_primary_teacher_id) || hasAbsentTeacher)
        ) {
          // Always try ID-based lookup first (disambiguates "S. Caksu" vs "I. Caksu")
          const mapped = teacherKurzelById.get(
            lesson.substitute_primary_teacher_id,
          );
          if (mapped) {
            subNames.push(mapped);
          } else if (lesson.substitute_primary_teacher_kurzel) {
            subNames.push(lesson.substitute_primary_teacher_kurzel);
          }
        } else if (
          lesson.substitute_primary_teacher_kurzel &&
          !originalTeacherIds.has(lesson.substitute_primary_teacher_id || "")
        ) {
          // Fallback: try ID lookup, then raw kurzel
          const mapped = lesson.substitute_primary_teacher_id
            ? teacherKurzelById.get(lesson.substitute_primary_teacher_id)
            : null;
          subNames.push(mapped || lesson.substitute_primary_teacher_kurzel);
        }

        if (subNames.length === 0 && lesson.substitute_primary_teacher) {
          const val = lesson.substitute_primary_teacher;
          if (typeof val === "string") {
            const mappedByName = nameToKurzel.get(val.trim().toLowerCase());
            subNames.push(mappedByName || val);
          }
        }

        // Filter begleitung teachers to exclude those already scheduled
        // Prefer ID-based lookup for disambiguation when IDs are available
        if (
          Array.isArray(lesson.substitute_begleitung_teacher_kurzels) &&
          lesson.substitute_begleitung_teacher_kurzels.length > 0
        ) {
          const begIds = Array.isArray(lesson.substitute_begleitung_teacher_ids)
            ? lesson.substitute_begleitung_teacher_ids
            : [];
          lesson.substitute_begleitung_teacher_kurzels.forEach(
            (k: string, idx: number) => {
              if (!k || typeof k !== "string") return;
              // Try ID lookup first for disambiguation
              const begId = begIds[idx];
              const mapped = begId ? teacherKurzelById.get(begId) : null;
              subNames.push(mapped || k);
            },
          );
        } else if (
          Array.isArray(lesson.substitute_begleitung_teacher_ids) &&
          lesson.substitute_begleitung_teacher_ids.length > 0
        ) {
          lesson.substitute_begleitung_teacher_ids.forEach((id: string) => {
            if (!originalTeacherIds.has(id)) {
              const mapped = teacherKurzelById.get(id);
              if (mapped) subNames.push(mapped);
            }
          });
        } else if (
          Array.isArray(lesson.substitute_begleitung_teachers) &&
          lesson.substitute_begleitung_teachers.length > 0
        ) {
          lesson.substitute_begleitung_teachers.forEach((n: string) => {
            if (typeof n === "string") {
              const mapped = nameToKurzel.get(n.trim().toLowerCase());
              subNames.push(mapped || n);
            }
          });
        }
      }

      const raum = {
        original: lesson.room || "",
        new: lesson.substitute_room || null,
      };
      const bemerkung: string | undefined =
        (lesson.substitute_comment &&
          String(lesson.substitute_comment).trim()) ||
        undefined;

      // Filter Fachkraft: remove non-absent teachers who are the substitute
      // (e.g. UB teacher promoted to primary substitute — they belong in Vertretung only)
      const subTeacherIds = new Set<string>();
      if (lesson.substitute_primary_teacher_id) {
        subTeacherIds.add(lesson.substitute_primary_teacher_id);
      }
      if (Array.isArray(lesson.substitute_begleitung_teacher_ids)) {
        lesson.substitute_begleitung_teacher_ids.forEach((id: string) =>
          subTeacherIds.add(id),
        );
      }
      const fehlendeKraftFiltered = fehlendeKraft.filter((t) => {
        if (t.isAbsent) return true; // Always show absent teachers (with strikethrough)
        // Get teacher ID from the processedTeachers map
        const tid = [...processedTeachers].find(
          (id) => (teacherKurzelById.get(id) || "") === t.name,
        );
        if (tid && subTeacherIds.has(tid)) return false; // Remove — they're the substitute
        return true;
      });

      // Track which substitute names are "internal" (were already on the lesson)
      // These get the → arrow in the display (e.g. UB taking over as primary)
      const internalSubNames = subNames.filter((_sn, idx) => {
        // Check if the substitute at this index was an original teacher
        if (idx === 0 && lesson.substitute_primary_teacher_id) {
          return originalTeacherIds.has(lesson.substitute_primary_teacher_id);
        }
        return false;
      });

      return {
        stunde: stunde || "-",
        klasse: lesson.class || "",
        fach: clampFach(lesson.course_name || lesson.subject || "-"),
        fehlendeKraft: fehlendeKraftFiltered,
        vertretung: subNames.length > 0 ? subNames : null,
        internalSubNames,
        isCancelled,
        isCourse: !!lesson.course_name,
        raum,
        bemerkung,
      };
    });
  }, [
    data?.lessons,
    periodsMapById,
    teacherKurzelById,
    nameToKurzel,
    clampFach,
  ]);

  const roleById = React.useMemo(() => {
    const map = new Map<string, string>();
    (data?.availableTeachers || []).forEach((t: any) => {
      const id: string | undefined = t?.id;
      if (!id) return;
      map.set(id, t?.role_name || "Teacher");
    });
    return map;
  }, [data?.availableTeachers]);

  const idByKurzel = React.useMemo(() => {
    const map = new Map<string, string>();
    (data?.availableTeachers || []).forEach((t: any) => {
      const k: string | undefined = t?.kurzung || t?.kurzel;
      if (k && t?.id) map.set(k, t.id);
    });
    return map;
  }, [data?.availableTeachers]);

  const idByNameLower = React.useMemo(() => {
    const map = new Map<string, string>();
    (data?.availableTeachers || []).forEach((t: any) => {
      const full = String(
        t?.name || `${t?.first_name || ""} ${t?.last_name || ""}`,
      ).trim();
      if (full && t?.id) map.set(full.toLowerCase(), t.id);
    });
    return map;
  }, [data?.availableTeachers]);

  const idByLastNameLower = React.useMemo(() => {
    const map = new Map<string, string>();
    (data?.availableTeachers || []).forEach((t: any) => {
      const lastName = (t?.last_name || "").trim();
      if (lastName && t?.id) map.set(lastName.toLowerCase(), t.id);
      // Also store disambiguated display name for lookup
      const firstName = (t?.first_name || "").trim();
      if (lastName && duplicateLastNames.has(lastName) && firstName && t?.id) {
        const display = `${firstName[0]}. ${lastName}`;
        map.set(display.toLowerCase(), t.id);
      }
    });
    return map;
  }, [data?.availableTeachers, duplicateLastNames]);

  const resolveRole = React.useCallback(
    (value?: string): string | undefined => {
      if (!value) return undefined;
      const byKurzel = idByKurzel.get(value);
      if (byKurzel) return roleById.get(byKurzel);
      const lower = String(value).trim().toLowerCase();
      const byName = idByNameLower.get(lower);
      if (byName) return roleById.get(byName);
      const byLastName = idByLastNameLower.get(lower);
      if (byLastName) return roleById.get(byLastName);
      return undefined;
    },
    [idByKurzel, idByNameLower, idByLastNameLower, roleById],
  );

  // Transform Aufsicht substitutions into display entries
  const aufsichtEntries = React.useMemo(() => {
    if (!data?.aufsichtSubstitutions?.length) return [];

    return data.aufsichtSubstitutions.map((asub: any) => {
      // Resolve absent teacher name via ID (disambiguated)
      const absentName =
        (asub.absent_staff_id && teacherKurzelById.get(asub.absent_staff_id)) ||
        asub.absent_last_name ||
        asub.absent_abbreviation ||
        "—";

      // Resolve substitute teacher name via ID (disambiguated)
      const subName =
        (asub.substitute_id && teacherKurzelById.get(asub.substitute_id)) ||
        asub.sub_last_name ||
        asub.sub_abbreviation ||
        null;

      const absentRole = asub.absent_staff_id
        ? resolveRole(absentName) || undefined
        : undefined;
      const subRole = subName ? resolveRole(subName) || undefined : undefined;

      return {
        stunde: asub.zeit_start
          ? String(asub.zeit_start).substring(0, 5).replace(":", "")
          : asub.zeit_label || "—",
        klasse: "Aufsicht",
        fach: asub.ort_name || "—",
        fehlendeKraft: [{ name: absentName, isAbsent: true, role: absentRole }],
        vertretung: subName ? [{ name: subName, role: subRole }] : null,
        internalSubNames: [] as string[],
        isCancelled: asub.is_cancelled || false,
        raum: { original: "", new: null },
        bemerkung: asub.comment || undefined,
        isAufsicht: true, // Flag for sort ordering
      };
    });
  }, [data?.aufsichtSubstitutions, teacherKurzelById, resolveRole]);

  // Enhance entries with role information
  const enhancedEntries = React.useMemo(() => {
    return entries.map((entry: any) => {
      const fehlendeKraft = entry.fehlendeKraft.map((f: any) => ({
        ...f,
        role: resolveRole(f.name),
      }));

      const vertretung = (entry.vertretung || []).map((name: string) => ({
        name,
        role: resolveRole(name),
      }));

      return {
        ...entry,
        fehlendeKraft,
        vertretung: vertretung.length > 0 ? vertretung : null,
      };
    });
  }, [entries, resolveRole]);

  // Merge lesson entries + aufsicht entries, with Aufsicht before Flex
  const allEntries = React.useMemo(() => {
    // Split lesson entries into regular and flex
    const regularEntries = enhancedEntries.filter(
      (e: any) =>
        e.klasse &&
        !e.klasse.toLowerCase().includes("flexband") &&
        e.klasse !== "",
    );
    const flexEntries = enhancedEntries.filter(
      (e: any) =>
        !e.klasse ||
        e.klasse.toLowerCase().includes("flexband") ||
        e.klasse === "",
    );

    // Order: Regular classes → Aufsicht → Flex
    return [...regularEntries, ...aufsichtEntries, ...flexEntries];
  }, [enhancedEntries, aufsichtEntries]);

  return {
    allEntries,
    absenteLehrkraefte,
    absenteKlassen,
    absentTagByKurzel,
    displayKurzel,
  };
};
